import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { ChevronDown, Building2 } from "lucide-react";

// Iter 31ll: chip "EG-XX-XXX · Cliente" nella Lista Rapportini.
// Admin: click → tendina con ricerca clienti → assegna/modifica il cliente
// della commessa e salva SUBITO (il backend propaga alla famiglia EG-XX,
// tranne il jolly EG-99 che è multi-cliente).
export default function CommessaClienteChip({
  codice,
  commessa, // oggetto commessa (può essere undefined se codice orfano)
  clienti = [],
  editable = false,
  onSaved,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const cliente = (commessa?.cliente || "").trim();

  const save = async (nomeCliente) => {
    if (!commessa?.id) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/commesse/${commessa.id}`, {
        codice: commessa.codice,
        descrizione: commessa.descrizione || "",
        cliente: nomeCliente,
        tipo_struttura: commessa.tipo_struttura || "cantiere",
        attiva: commessa.attiva !== false,
      });
      toast.success(`Cliente "${data.cliente}" assegnato a ${commessa.codice}`);
      setOpen(false);
      setQ("");
      onSaved?.(data);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Salvataggio non riuscito");
    } finally {
      setBusy(false);
    }
  };

  const label = (
    <span className="inline-flex items-center gap-1 max-w-full">
      <b>{codice}</b>
      <span className={cliente ? "text-slate-500" : "text-amber-600 font-semibold"}>
        · {cliente || "assegna cliente"}
      </span>
      {editable && commessa?.id && <ChevronDown className="h-3 w-3 opacity-60" />}
    </span>
  );

  if (!editable || !commessa?.id) {
    return (
      <span
        className="text-sm text-slate-600 truncate"
        data-testid={`commessa-chip-${codice}`}
      >
        {label}
      </span>
    );
  }

  const filtered = clienti.filter((c) =>
    (c.nome || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`text-sm truncate rounded px-1.5 py-0.5 -mx-1 transition-colors hover:bg-blue-50 ${
            cliente ? "text-slate-600" : "bg-amber-50 border border-amber-200"
          }`}
          data-testid={`commessa-chip-${codice}`}
          title="Clicca per assegnare/modificare il cliente"
          onClick={(e) => e.stopPropagation()}
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-500 px-1 pb-1.5 flex items-center gap-1">
          <Building2 className="h-3.5 w-3.5" /> Cliente per {codice}
        </div>
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca cliente…"
          className="h-9 mb-1.5"
          data-testid={`cliente-search-${codice}`}
        />
        <div className="max-h-56 overflow-y-auto space-y-0.5">
          {filtered.length === 0 && (
            <div className="text-xs text-slate-400 px-1 py-2">
              Nessun cliente trovato in anagrafica
            </div>
          )}
          {filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              disabled={busy}
              onClick={() => save(c.nome)}
              data-testid={`cliente-option-${c.id}`}
              className={`w-full text-left text-sm px-2 py-1.5 rounded hover:bg-blue-50 ${
                cliente && c.nome === cliente ? "bg-blue-50 font-bold" : ""
              }`}
            >
              {c.nome}
              {c.citta ? (
                <span className="text-xs text-slate-400"> · {c.citta}</span>
              ) : null}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
